export const IDEMPOTENCY_SESSION_KEY = "order-idempotency-key";

/**
 * Returns a stable idempotency key for the current cart contents, persisted in sessionStorage.
 *
 * The key is keyed to a fingerprint derived from each item's uuid and purchaseOrderId (sorted,
 * so item order doesn't matter). A fingerprint mismatch (caused by changing items or PO IDs)
 * generates a new key, preventing a prior submission from being replayed for a different order.
 * Users/companies/locations that don't use PO IDs are covered by the item uuids alone.
 *
 * Note: **always call `sessionStorage.removeItem(IDEMPOTENCY_SESSION_KEY)` after a confirmed order placement.**
 */
export function getOrCreateIdempotencyKey(
    items: ReadonlyArray<{ uuid: string; purchaseOrderId: string | null }>,
    generateId: () => string = () => crypto.randomUUID()
): string {
    const fingerprint = [...items]
        .map((li) => `${li.uuid}:${li.purchaseOrderId ?? ""}`)
        .sort()
        .join(",");

    const stored = sessionStorage.getItem(IDEMPOTENCY_SESSION_KEY);

    if (stored) {
        try {
            const { key, fingerprint: storedFingerprint } = JSON.parse(stored) as {
                key: string;
                fingerprint: string;
            };
            if (storedFingerprint === fingerprint) return key;
        } catch {
            // Ignore malformed storage value and regenerate below.
        }
    }

    const key = generateId();
    sessionStorage.setItem(IDEMPOTENCY_SESSION_KEY, JSON.stringify({ key, fingerprint }));
    return key;
}

